import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { dateInputToIsoFormat, isoToDateInputFormat } from '../../../../models/utils/dateUtils';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';
import { useUpdateTemplateMutation } from '../../../../queries/Templates/useUpdateTemplateMutation';
import { DateInput } from '../../../components-v2/DateInput';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { CalendarIcon } from '../../svgs/icons/CalendarIcon';
import { EditItemPenIcon } from '../../svgs/icons/EditItemPenIcon';
import { ExpiryIcon } from '../../svgs/icons/ExpiryIcon';
import { PlusIcon } from '../../svgs/icons/PlusIcon';
import { SimpleTooltip } from '../../Tooltip';
import { styleSheet } from './styles';

interface ITemplateDateFieldProps {
	template: ITemplateCard;
	templateType: Api.TemplateType;
	fieldName: 'startDate' | 'expirationDate';
	label: string;
	onSuccess?: () => void;
	styles?: StyleDeclarationValue[];
}

export const TemplateDateField: React.FC<ITemplateDateFieldProps> = ({
	template,
	templateType,
	fieldName,
	label,
	onSuccess,
	styles: customStyles = [],
}) => {
	// Read from schedule object
	const value = template.schedule?.[fieldName];
	const [isOpen, setIsOpen] = useState(false);
	const [localValue, setLocalValue] = useState(isoToDateInputFormat(value));
	// Track displayed date separately for optimistic updates
	const [optimisticDisplayDate, setOptimisticDisplayDate] = useState<string | null>(null);
	const { pushApiError } = useErrorMessages();
	const userSession = useUserSession();

	// Sync local value when prop changes (e.g., after refetch)
	React.useEffect(() => {
		setLocalValue(isoToDateInputFormat(value));
		setOptimisticDisplayDate(null); // Clear optimistic state when real data arrives
	}, [value]);

	const updateMutation = useUpdateTemplateMutation({
		onSuccess: () => {
			setIsOpen(false);
			onSuccess?.();
		},
		onError: error => {
			// Reset to original value on error
			setLocalValue(isoToDateInputFormat(value));
			setOptimisticDisplayDate(null);
			pushApiError(error);
		},
	});

	const hasValue = !!value || !!optimisticDisplayDate;
	const isExpirationDate = fieldName === 'expirationDate';

	const handleDateChange = async (newDateString: string) => {
		// Optimistically update the UI immediately
		setLocalValue(newDateString);
		if (newDateString) {
			const parsedDate = moment(newDateString, 'YYYY-MM-DD', true);
			setOptimisticDisplayDate(parsedDate.isValid() ? parsedDate.format('MM/DD/YY') : null);
		} else {
			setOptimisticDisplayDate(null);
		}

		const isoValue = dateInputToIsoFormat(newDateString);

		// Build the schedule object, preserving the other date field
		const schedule: Api.ISuggestedSendSchedule = {
			startDate: fieldName === 'startDate' ? isoValue : (template.schedule?.startDate ?? null),
			expirationDate: fieldName === 'expirationDate' ? isoValue : (template.schedule?.expirationDate ?? null),
		};

		try {
			// Fetch the full template to ensure all fields (e.g., subject) are preserved in the update
			const fullTemplate = await userSession.webServiceHelper.callAsync<Api.ITemplate>(
				`template/${encodeURIComponent(template.id)}`,
				'GET'
			);
			const templateUpdate: Api.ITemplate = {
				...fullTemplate,
				templateType,
				schedule,
			};
			updateMutation.mutate({ template: templateUpdate });
		} catch (error) {
			setLocalValue(isoToDateInputFormat(value));
			setOptimisticDisplayDate(null);
			pushApiError(error as Api.IOperationResultNoValue);
		}
	};

	const handleClearDate = () => {
		handleDateChange('');
	};

	// Use optimistic date if available, otherwise use actual value
	const displayDate = optimisticDisplayDate ?? (value ? moment(value).format('MM/DD/YY') : null);
	const tooltipText = hasValue ? `Edit ${label}` : `Add ${label}`;

	// Render the appropriate icon based on field type
	const renderIcon = () => {
		if (isExpirationDate) {
			return <ExpiryIcon className={css(styleSheet.calendarIcon)} fill='currentColor' width={14} height={14} />;
		}
		return <CalendarIcon className={css(styleSheet.calendarIcon)} />;
	};

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<SimpleTooltip title={tooltipText}>
				<PopoverTrigger asChild>
					<button
						className={css(styleSheet.triggerButton, ...customStyles)}
						disabled={updateMutation.isPending}
						type='button'
					>
						<span className={css(styleSheet.iconContainer)}>
							{renderIcon()}
							<span className={css(styleSheet.badge)}>
								{hasValue ? (
									<EditItemPenIcon className={css(styleSheet.badgeIcon)} fillColor='currentColor' />
								) : (
									<PlusIcon className={css(styleSheet.badgeIcon)} fillColor='currentColor' width={6} height={6} />
								)}
							</span>
						</span>
						{displayDate && <span className={css(styleSheet.dateText)}>{displayDate}</span>}
					</button>
				</PopoverTrigger>
			</SimpleTooltip>
			<PopoverContent side='bottom' align='start'>
				<div className={css(styleSheet.popoverContent)}>
					<div className={css(styleSheet.popoverLabel)}>{label}</div>
					<DateInput value={localValue} onChange={handleDateChange} />
					{hasValue && (
						<button
							className={css(styleSheet.clearButton)}
							onClick={handleClearDate}
							disabled={updateMutation.isPending}
							type='button'
						>
							Clear date
						</button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};
